import React from 'react';
import { useNavigate } from 'react-router-dom';

export interface ErrorAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary';
}

export interface ErrorStateProps {
  title?: string;
  message: string;
  details?: string;
  icon?: 'error' | 'network' | 'notFound' | 'permission';
  actions?: ErrorAction[];
  showHomeButton?: boolean;
  showRetryButton?: boolean;
  onRetry?: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  title = 'Something went wrong',
  message,
  details,
  icon = 'error',
  actions = [],
  showHomeButton = true,
  showRetryButton = true,
  onRetry,
}) => {
  const navigate = useNavigate();

  const renderIcon = () => {
    const iconClass = 'w-12 h-12';

    switch (icon) {
      case 'network':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
            />
          </svg>
        );
      case 'notFound':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'permission':
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        );
      default:
        return (
          <svg className={iconClass} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        );
    }
  };

  const defaultActions: ErrorAction[] = [];

  if (showRetryButton && onRetry) {
    defaultActions.push({
      label: 'Try Again',
      onClick: onRetry,
      variant: 'primary',
    });
  }

  if (showHomeButton) {
    defaultActions.push({
      label: 'Go to Home',
      onClick: () => navigate('/'),
      variant: 'secondary',
    });
  }

  const allActions = [...actions, ...defaultActions];

  return (
    <div className="flex flex-col items-center justify-center p-8 animate-fadeIn">
      {/* Icon */}
      <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6 text-red-500 dark:text-red-400">
        {renderIcon()}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">{title}</h3>

      {/* Message */}
      <p className="text-gray-600 dark:text-gray-300 text-center max-w-md mb-2">{message}</p>

      {/* Details */}
      {details && (
        <p className="text-sm text-gray-400 dark:text-gray-500 text-center max-w-md mb-6">
          {details}
        </p>
      )}

      {/* Actions */}
      {allActions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
          {allActions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                action.variant === 'primary'
                  ? 'bg-blue-500 hover:bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Help text */}
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-6">
        If this problem persists, please try again later or contact support.
      </p>
    </div>
  );
};

export default ErrorState;
